import { useEffect, useState, useCallback, useMemo } from 'react'
import { fetchUserTransferOrganizations } from '../../../services/userServices'
import { IOrganization } from '../../Layout/Navbar/interfaces'

interface UseFetchTransferOrganizationsProps {
    templateId: string
    orgId?: string
    excludeOrgs?: string[]
    includeVirtualOrgs?: boolean
}

const PAGE_SIZE = 30

export const useFetchTransferOrganizations = ({
    templateId,
    orgId,
    excludeOrgs,
    includeVirtualOrgs = false,
}: UseFetchTransferOrganizationsProps): {
    orgsList: IOrganization[]
    isFetching: boolean
    hasMore: boolean
    onSearch: (_: string, text: string) => void
    fetchNextPage: () => void
} => {
    const [fetchedOrgs, setFetchedOrgs] = useState<any[]>([])
    const [pageNumber, setPageNumber] = useState<number>(1)
    const [searchText, setSearchText] = useState<string>('')
    const [hasMore, setHasMore] = useState<boolean>(false)
    const [isFetching, setIsFetching] = useState<boolean>(false)
    const orgsList = useMemo(() => [...fetchedOrgs], [fetchedOrgs])
    useEffect(() => {
        const fetchRecords = async () => {
            setIsFetching(true)
            const orgsResponse = await fetchUserTransferOrganizations({
                templateId,
                pageNumber,
                size: PAGE_SIZE,
                currentOrgId: orgId,
                searchText: searchText?.trim(),
                includeVirtualOrgs,
            })
            const orgs = orgsResponse?.organizations.filter(
                (i) => !excludeOrgs?.includes(i.id),
            )

            setFetchedOrgs((prev) =>
                pageNumber === 1 ? orgs : [...prev, ...orgs],
            )
            setHasMore(orgsResponse?.organizations?.length === PAGE_SIZE)
            setIsFetching(false)
        }
        fetchRecords()
    }, [pageNumber, searchText, orgId])

    const onSearch = useCallback((_: string, text: string) => {
        setSearchText(text)
        setPageNumber(1)
    }, [])

    const fetchNextPage = useCallback(() => {
        if (!isFetching && hasMore) {
            setPageNumber((p) => p + 1)
        }
    }, [isFetching, hasMore])

    return {
        orgsList,
        isFetching,
        hasMore,
        onSearch,
        fetchNextPage,
    }
}
